<template>
  <b-modal
    :visible="visible"
    :title="title"
    centered
    @shown="focusInput"
    @hidden="$emit('close')"
  >
    <b-form-input
      ref="nameInput"
      v-model="name"
      maxlength="60"
      placeholder="Folder name"
      aria-label="Folder name"
      :state="error ? false : null"
      @keyup.enter="save"
    />
    <b-form-invalid-feedback :state="error ? false : null">
      {{ error }}
    </b-form-invalid-feedback>

    <div v-if="parentOptions.length" class="parent-picker">
      <label class="color-label" for="folder-parent">Inside</label>
      <b-form-select
        id="folder-parent"
        v-model="parentId"
        :options="parentSelectOptions"
        data-test="folder-parent"
      />
    </div>

    <p class="color-label">Colour</p>
    <div
      class="color-picker"
      role="radiogroup"
      aria-label="Folder colour"
      data-test="folder-colors"
    >
      <button
        type="button"
        class="swatch swatch-none"
        :class="{ selected: !color }"
        role="radio"
        :aria-checked="!color ? 'true' : 'false'"
        aria-label="No colour"
        title="No colour"
        data-test="color-none"
        @click="color = null"
      >
        <b-icon icon="folder-fill" />
      </button>
      <button
        v-for="option in colors"
        :key="option.id"
        type="button"
        class="swatch"
        :class="{ selected: color === option.id }"
        :style="{ backgroundColor: option.hex }"
        role="radio"
        :aria-checked="color === option.id ? 'true' : 'false'"
        :aria-label="option.label"
        :title="option.label"
        :data-test="`color-${option.id}`"
        @click="color = option.id"
      >
        <b-icon v-if="color === option.id" icon="check" />
      </button>
    </div>

    <template #modal-footer>
      <b-button variant="outline-secondary" @click="$emit('close')">
        CANCEL
      </b-button>
      <b-button variant="primary" :disabled="saving" @click="save">
        {{ saveLabel }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { FOLDER_COLORS } from '@/utils/folderColors'

export default {
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '' },
    initialName: { type: String, default: '' },
    initialColor: { type: String, default: null },
    initialParentId: { type: String, default: null },
    // Folders this one can go inside: { id, path }. Empty hides the picker.
    parentOptions: { type: Array, default: () => [] },
    saveLabel: { type: String, default: 'SAVE' },
    saving: { type: Boolean, default: false },
    error: { type: String, default: '' },
  },

  data() {
    return {
      name: '',
      color: null,
      parentId: null,
      colors: FOLDER_COLORS,
    }
  },

  computed: {
    parentSelectOptions() {
      return [{ value: null, text: 'Top level' }].concat(
        this.parentOptions.map((folder) => ({
          value: folder.id,
          text: folder.path,
        })),
      )
    },
  },

  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.name = this.initialName
        this.color = this.initialColor || null
        this.parentId = this.initialParentId || null
      }
    },
  },

  methods: {
    focusInput() {
      if (this.$refs.nameInput) this.$refs.nameInput.focus()
    },

    save() {
      this.$emit(
        'save',
        this.name,
        this.color,
        this.parentId === null ? null : Number(this.parentId),
      )
    },
  },
}
</script>

<style scoped>
.color-label {
  margin: 16px 0 8px;
  color: #6b7280;
  font-size: 14px;
  font-weight: 700;
}

.parent-picker .color-label {
  display: block;
  margin-top: 16px;
}

.color-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

/* Plain buttons, not .btn: the global .btn has a fixed 56px height. */
.swatch {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 50%;
  color: #ffffff;
  cursor: pointer;
}

.swatch.selected {
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #374151;
}

.swatch-none {
  background-color: #ffffff;
  border-color: #d1d5db;
  color: #9ca3af;
}
</style>
